/**
 * The optimistic overlay: the Cells this page has painted and the server has not
 * echoed back yet. Every Cell in it is held until the server echoes it, someone
 * else's write arrives, or 2 s pass, whichever comes first, so nothing
 * unconfirmed stays on screen longer than 2 s and a dropped paint shows as a
 * revert. A snapshot replaces everything, which clears the overlay with it.
 *
 * The Grid this returns is the Grid the page draws and the Grid it sounds: your
 * own paint reaches your speaker with no network hop. Pure functions only, and
 * each hands the same value back when nothing changed, so an idle frame is free.
 */

import type { PaintCells } from "./world.ts"
import { isCellIndex } from "./world.ts"

/** Nothing unconfirmed stays on screen longer than this. */
export const OVERLAY_MS = 2000

/** One Cell held: the value shown, and the page time it was painted. */
export interface Unconfirmed {
  readonly value: number
  readonly at: number
}

export type Overlay = ReadonlyMap<number, Unconfirmed>

export const emptyOverlay: Overlay = new Map()

/** A local paint shows at once. Painting a Cell again restarts its 2 s. */
export const applyPaint = (overlay: Overlay, cells: PaintCells, at: number): Overlay => {
  if (cells.length === 0) return overlay
  const next = new Map(overlay)
  for (const [cell, value] of cells) if (isCellIndex(cell)) next.set(cell, { value, at })
  return next
}

/**
 * A `cell` message. Your own echo confirms a Cell only when it carries the value
 * you are showing: an echo of a paint you have since painted over is stale and
 * would revert the newer one. Anyone else's write wins outright, whatever it
 * carries, because the server has since taken their value.
 */
export const applyEcho = (overlay: Overlay, cells: PaintCells, ownEcho: boolean): Overlay => {
  if (overlay.size === 0) return overlay
  let next: Map<number, Unconfirmed> | undefined
  for (const [cell, value] of cells) {
    const held = overlay.get(cell)
    if (held === undefined || (ownEcho && held.value !== value)) continue
    next ??= new Map(overlay)
    next.delete(cell)
  }
  return next ?? overlay
}

/** Drop every Cell held longer than 2 s. */
export const dropExpired = (overlay: Overlay, now: number): Overlay => {
  let next: Map<number, Unconfirmed> | undefined
  for (const [cell, held] of overlay) {
    if (now - held.at < OVERLAY_MS) continue
    next ??= new Map(overlay)
    next.delete(cell)
  }
  return next ?? overlay
}

/** The Grid the page draws and sounds: the server's, with the overlay written over it. */
export const drawnGrid = (grid: string, overlay: Overlay): string => {
  if (overlay.size === 0) return grid
  const cells = [...grid]
  for (const [cell, held] of overlay) cells[cell] = String(held.value)
  return cells.join("")
}
